/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router-dom';

import { AutocompleteFormField } from '@components/form-fields/AutocompleteFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '@features/production/proposal/drawers/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';

import {
  useGetBusinessPartnerPersons,
  useGetPersonAddressList
} from '../amendment.slice';
import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(2, 49%)`,
    gap: '2%',
    alignItems: 'center'
  },
  field: {
    width: '100%',
    marginRight: '25px !important',
    minWidth: '195px'
  }
}));

type Props = {
  disabledForm?: boolean;
  primaryBPId?: string;
  lineExternalCode?: string;
  personsOptions: Record<string, string>; // personId -> label
  setPersonsOptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
};

const mergeDedupByLabel = (
  base: Record<string, string>,
  incoming: Record<string, string>
): Record<string, string> => {
  const out: Record<string, string> = { ...base };
  const seen = new Set<string>(Object.values(base));
  for (const [id, label] of Object.entries(incoming || {})) {
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out[id] = label;
  }

  return out;
};

const formatAddress = (it: any) => {
  const { building, street, city } = it?.address || {};
  const country = it?.country?.title;
  const zone = it?.zone?.title;

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

const PayerWidgetAtom: React.FC<Props> = ({
  disabledForm,
  primaryBPId,
  lineExternalCode,
  personsOptions,
  setPersonsOptions
}) => {
  const { classes } = useStyles();
  const { id: amendmentId } = useParams<{ id: string }>();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    handleBlur
  } = useFormikContext<FormValues>();

  const payerValues = values?.payerOwner?.payer;
  const introducedByValues = {} as any;
  const payerErrors = (errors?.payerOwner as any)?.payer ?? {};
  const payerTouched = (touched?.payerOwner as any)?.payer ?? {};

  const ownerIsDifferentVal = Boolean(
    values?.payerOwner?.ownerIsDifferent ?? false
  );

  const status = values.status;
  const isDisabled =
    status === 'CONFIRMED' || status === 'CANCELLED' || !!disabledForm;

  const [personsQuery] = useGetBusinessPartnerPersons();
  const [addressesQuery] = useGetPersonAddressList();

  const [lists, setLists] = useState<{
    payerPersons: Record<string | number, string>;
    payerAddresses: Record<string | number, string>;
  }>({ payerPersons: {}, payerAddresses: {} });

  const mainbusinessPartnerID = useMemo(() => primaryBPId ?? '', [primaryBPId]);
  const payerPath = (k: string) => `payerOwner.payer.${k}`;

  const labelFetchedForIdRef = useRef<Record<string | number, boolean>>({});
  const addressSeededForIdRef = useRef<Record<string | number, boolean>>({});
  const addrTextHydratedRef = useRef<Record<string, boolean>>({});

  const seededRef = useRef(false);

  const seedPersonsPool = useCallback(async () => {
    if (seededRef.current) return;
    seededRef.current = true;

    try {
      const res = await personsQuery({
        variables: {
          searchKeyword: '',
          mainbusinessPartnerID
        }
      });

      const items = res?.data?.salesforce?.getBusinessPartnerPersons || [];
      const pool: Record<string, string> = {};
      for (const i of items) {
        const label = `${i.code} - ${i.fullName}`;
        if (label) pool[i.id] = label;
      }

      if (Object.keys(pool).length) {
        setPersonsOptions((prev) => mergeDedupByLabel(prev, pool));
      }
    } catch {
      /* ignore */
    }
  }, [personsQuery, mainbusinessPartnerID, setPersonsOptions]);

  useEffect(() => {
    seedPersonsPool();
  }, [seedPersonsPool]);

  const fetchAddresses = async (personId: string) => {
    const res = await addressesQuery({
      variables: { selectedPersonID: personId }
    });
    const items = res?.data?.salesforce?.getPersonAddressList?.items || [];
    const map: Record<string | number, string> = {};
    let primary: string | number = '';
    let primaryText = '';

    items.forEach((it: any) => {
      const addr = formatAddress(it);
      map[it.id] = addr;
      if (it.isPrimary && !primary) {
        primary = it.id;
        primaryText = addr;
      }
    });

    if (!primary && items[0]) {
      primary = items[0].id;
      primaryText = formatAddress(items[0]);
    }

    return { map, primary, primaryText };
  };

  // Seed initial persons/addresses for current payer
  useEffect(() => {
    const id = payerValues?.id;
    const addr = payerValues?.addressId;

    setLists((prev) => {
      const next = { ...prev };

      if (id && payerValues?.fullName && !next.payerPersons[id]) {
        next.payerPersons = {
          ...next.payerPersons,
          [id]: payerValues.fullName
        };
      }

      if (addr && payerValues?.addressDetails && !next.payerAddresses[addr]) {
        next.payerAddresses = {
          ...next.payerAddresses,
          [addr]: payerValues.addressDetails
        };
      }

      return next;
    });

    (async () => {
      if (!id) return;

      if (
        mainbusinessPartnerID &&
        !lists?.payerPersons?.[id] &&
        !labelFetchedForIdRef.current[id]
      ) {
        labelFetchedForIdRef.current[id] = true;
        const labelFromSeed = personsOptions?.[id];

        if (labelFromSeed) {
          setLists((prev) => ({
            ...prev,
            payerPersons: { ...prev.payerPersons, [id]: labelFromSeed }
          }));
          if (!payerValues?.fullName) {
            await setFieldValue(payerPath('fullName'), labelFromSeed, false);
          }
        }
      }

      if (id && !addr && !addressSeededForIdRef.current[id]) {
        addressSeededForIdRef.current[id] = true;
        const { map, primary, primaryText } = await fetchAddresses(String(id));

        setLists((prev) => ({
          ...prev,
          payerAddresses: { ...prev.payerAddresses, ...map }
        }));

        await setFieldValue(payerPath('payerAddress'), primary || '', false);
        await setFieldValue(
          payerPath('addressDetails'),
          primaryText || '',
          false
        );
        setFieldError(payerPath('payerAddress'), '');
        setFieldTouched(payerPath('payerAddress'), true, false);
      }

      const pairKey = `${id}|${addr}`;
      if (
        id &&
        addr &&
        !lists?.payerAddresses?.[addr] &&
        !addrTextHydratedRef.current[pairKey]
      ) {
        addrTextHydratedRef.current[pairKey] = true;

        const res = await addressesQuery({
          variables: { selectedPersonID: id }
        });
        const items = res?.data?.salesforce?.getPersonAddressList?.items || [];
        const match = items.find((a: any) => a.id === addr);

        if (match) {
          const text = formatAddress(match);
          setLists((prev) => ({
            ...prev,
            payerAddresses: { ...prev.payerAddresses, [addr]: text }
          }));
          if (!payerValues?.addressDetails) {
            await setFieldValue(payerPath('addressDetails'), text, false);
          }
        }
      }
    })();
  }, [payerValues?.id, payerValues?.addressId, mainbusinessPartnerID]);

  const onPersonChange = async (v: any) => {
    await setFieldValue(payerPath('id'), v, false);
    await setFieldValue('payerOwner.ownerIsDifferent', true, false);

    labelFetchedForIdRef.current = {};
    addressSeededForIdRef.current = {};
    addrTextHydratedRef.current = {};

    const label =
      personsOptions[v] ||
      (payerValues?.fullName && v === payerValues?.id
        ? payerValues.fullName
        : '') ||
      '';
    if (label) await setFieldValue(payerPath('fullName'), label, false);

    if (!isEmpty(v)) {
      const { map, primary, primaryText } = await fetchAddresses(String(v));
      setLists((prev) => ({
        ...prev,
        payerAddresses: { ...prev.payerAddresses, ...map }
      }));
      await setFieldValue(payerPath('addressId'), primary || '', false);
      await setFieldValue(
        payerPath('addressDetails'),
        primaryText || '',
        false
      );

      const firstPersonLabel = personsOptions?.[v] || '';
      if (!introducedByValues?.id && firstPersonLabel) {
        await setFieldValue('payerOwner.introducedBy.id', v, false);
        await setFieldValue(
          'payerOwner.introducedBy.fullName',
          firstPersonLabel,
          false
        );
      }
    } else {
      setLists((prev) => ({ ...prev, payerAddresses: {} }));
      await setFieldValue(payerPath('addressId'), '', false);
      await setFieldValue(payerPath('addressDetails'), '', false);
    }
  };

  const onAddressChange = async (v: any) => {
    await setFieldValue(payerPath('payerAddress'), v, false);
    const text = lists.payerAddresses[v] || '';
    await setFieldValue(payerPath('addressDetails'), text, false);
  };

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState(false);

  return (
    <WidgetSection title="Payer">
      <div className={classes.fieldRow}>
        {newPersonDrawerOpen && (
          <ProposalPolicyPersonDrawer
            open
            onClose={() => setNewPersonDrawerOpen(false)}
            section="payer"
            mainBusinessPartnerId={primaryBPId ?? ''}
            lineExternalCode={lineExternalCode ?? ''}
            onSubmitSuccess={async (person) => {
              const personId = person.id;
              const fullName = `${person.code} - ${person.fullName}`;
              setPersonsOptions((prev) =>
                mergeDedupByLabel(prev, { [personId]: fullName })
              );

              const { map, primary } = await fetchAddresses(String(personId));
              setLists((prev) => ({
                ...prev,
                payerAddresses: { ...prev.payerAddresses, ...map }
              }));
              await setFieldValue(payerPath('id'), personId, false);
              await setFieldValue(payerPath('fullName'), fullName, false);
              await setFieldValue(payerPath('addressId'), primary || '', true);

              setNewPersonDrawerOpen(false);
            }}
          />
        )}

        {newAddressDrawerOpen && (
          <PersonAddressDrawer
            open
            isProposal
            onClose={() => setNewAddressDrawerOpen(false)}
            personId={payerValues?.id}
            onSubmitSuccess={async (addrId, addrText) => {
              const personId = payerValues?.id;

              setLists((prev) => ({
                ...prev,
                payerAddresses: { ...prev.payerAddresses, [addrId]: addrText }
              }));

              await setFieldValue(payerPath('addressId'), addrId, false);
              await setFieldValue(payerPath('addressDetails'), addrText, false);

              const insureds = (values as any)?.insureds || [];
              for (let i = 0; i < insureds.length; i++) {
                if (insureds[i].id === personId) {
                  await setFieldValue(
                    `insureds[${i}].addressId`,
                    addrId,
                    false
                  );
                  await setFieldValue(
                    `insureds[${i}].addressDetails`,
                    addrText,
                    false
                  );
                }
              }

              const beneficiaries = (values as any)?.beneficiaries || [];
              for (let i = 0; i < beneficiaries.length; i++) {
                if (beneficiaries[i].id === personId) {
                  await setFieldValue(
                    `beneficiaries[${i}].addressId`,
                    addrId,
                    false
                  );
                  await setFieldValue(
                    `beneficiaries[${i}].addressDetails`,
                    addrText,
                    false
                  );
                }
              }

              if ((values as any)?.payerOwner?.owner?.id === personId) {
                await setFieldValue(
                  'payerOwner.owner.addressId',
                  addrId,
                  false
                );
                await setFieldValue(
                  'payerOwner.owner.addressDetails',
                  addrText,
                  false
                );
              }

              setNewAddressDrawerOpen(false);
            }}
            onSuccess={() => undefined}
          />
        )}

        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Person'}
          addSpace={!isDisabled}
          onLinkClick={(e?: any) => {
            e?.preventDefault?.();
            setNewPersonDrawerOpen(true);
          }}
        >
          <AutocompleteFormField
            name="payerOwner.payer.id"
            title="Payer*"
            placeholder="Type to search for Person"
            value={payerValues?.id}
            error={payerTouched?.id ? payerErrors?.id : ''}
            material
            disabled={isDisabled}
            onChange={onPersonChange}
            onBlur={(_, e) => handleBlur(e)}
            initialOptions={personsOptions}
            fetchOptions={async (
              searchKeyword
            ): Promise<Record<string, string>> => {
              const q = (searchKeyword ?? '').trim();
              const res = await personsQuery({
                variables: {
                  searchKeyword: q,
                  mainbusinessPartnerID
                }
              });

              const items =
                res?.data?.salesforce?.getBusinessPartnerPersons || [];
              const deduped: Record<string, string> = {};
              const seen = new Set<string>(Object.values(personsOptions || {}));

              for (const i of items) {
                const label = `${i.code} - ${i.fullName}`;
                if (!label || seen.has(label)) continue;
                seen.add(label);
                deduped[i.id] = label;
              }

              setPersonsOptions((prev) => mergeDedupByLabel(prev, deduped));

              return deduped;
            }}
          />
        </WithLinkFormField>

        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Address'}
          addSpace={!isDisabled}
          onLinkClick={(e?: any) => {
            e?.preventDefault?.();
            setNewAddressDrawerOpen(true);
          }}
        >
          <SelectFormField
            key={`payerOwner.payer.payerAddress-${payerValues?.addressId}`}
            name="payerOwner.payer.addressId"
            title="Payer Address*"
            placeholder="Payer Address"
            value={payerValues?.addressId}
            error={payerTouched?.addressId ? payerErrors?.addressId : ''}
            material
            disabled
            onChange={onAddressChange}
            onBlur={(_, e) => handleBlur(e)}
            selectOptions={lists.payerAddresses}
          />
        </WithLinkFormField>
      </div>

      <div style={{ width: 235 }}>
        <SwitchFormField
          key="payerOwner.ownerIsDifferent"
          name="payerOwner.ownerIsDifferent"
          title="Owner is different from Payer"
          onChange={async (_, nextChecked) => {
            if (nextChecked) {
              await seedPersonsPool();
              await setFieldValue('payerOwner.ownerIsDifferent', true, false);
              await setFieldValue('payerOwner.owner.id', '', false);
              await setFieldValue('payerOwner.owner.addressId', '', false); // FIXED: was ownerAddress
              await setFieldValue('payerOwner.owner.addressDetails', '', false);
              await setFieldValue('payerOwner.owner.fullName', '', false);

              return;
            }

            await setFieldValue('payerOwner.ownerIsDifferent', false, false);
            await setFieldValue('payerOwner.owner.id', '', false);
            await setFieldValue('payerOwner.owner.addressId', '', false); // FIXED: was ownerAddress
            await setFieldValue('payerOwner.owner.addressDetails', '', false);
            await setFieldValue('payerOwner.owner.fullName', '', false);
          }}
          className={classes.field}
          disabled={isDisabled}
          value={ownerIsDifferentVal}
        />
      </div>
    </WidgetSection>
  );
};

export { PayerWidgetAtom };
